import React, { useEffect, useState } from 'react'

import stylesModule from 'app/components/icons/circular-progress-icon/circular-progress-icon.module.scss'
import className from 'classnames/bind'
import useHover from 'app/hooks/use-hover.hook'

const cx = className.bind(stylesModule)

type CircularProgressIconProps = {
  width?: number
  strokeWidth?: number
  percentage: number
  fillColor: string
  hoverColor?: string
  progressColor: string
  isMuteIcon?: boolean
}

const CircularProgressIcon = ({
  width = 57,
  strokeWidth = 5,
  percentage,
  fillColor,
  hoverColor,
  progressColor,
  isMuteIcon = false,
}: CircularProgressIconProps) => {
  // SVG centers the stroke width on the radius, subtract out so circle fits in square
  const radius = (width - strokeWidth) / 2
  // Enclose circle in a circumscribing square
  const viewBox = `0 0 ${width} ${width}`
  // Arc length at 100% coverage is the circle circumference
  const dashArray = radius * Math.PI * 2
  // Scale 100% coverage overlay with the actual percent
  const dashOffset = dashArray - (dashArray * percentage) / 100

  const [color, setColor] = useState<string | null>(null)
  const [hoverRef, isHovered] = useHover<SVGSVGElement>()

  useEffect(() => {
    setColor(fillColor)
  }, [fillColor])

  function onMouseOver() {
    setColor(hoverColor || '')
  }

  function onMouseLeave() {
    setColor(fillColor)
  }

  useEffect(() => {
    if (isHovered) {
      onMouseOver()
    } else {
      onMouseLeave()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverRef, isHovered])

  return (
    <svg width={width} height={width} viewBox={viewBox} ref={hoverRef}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={width}
        viewBox="0 0 63 63"
      >
        <g fill="none" fillRule="evenodd" transform="translate(3 3)">
          <circle
            cx="28.5"
            cy="28.5"
            r="28.5"
            fill={color || undefined}
            stroke={color || undefined}
            strokeWidth="5"
          />
          {!isMuteIcon ? null : (
            <>
              <path
                id="mute-path-wrap"
                stroke="#FF435F"
                strokeLinecap="round"
                strokeWidth="6"
                d="M10.804 16.063l36 24"
              />
              <path
                id="mute-path"
                stroke="#FFF"
                strokeLinecap="round"
                strokeWidth="3"
                d="M10.804 16.063l36 24"
              />
            </>
          )}
          <path
            fill="#FFF"
            d="M16.776 22.078l13.105-9.142a.581.581 0 0 1 .923.469v30.19a.58.58 0 0 1-.923.469l-13.105-9.142h-6.22a.58.58 0 0 1-.581-.58V22.658c0-.32.26-.58.58-.58h6.221zm21.47-6.115a1.451 1.451 0 1 1 1.88-2.212c4.351 3.696 6.899 9.022 6.899 14.749s-2.548 11.053-6.9 14.75a1.452 1.452 0 0 1-1.88-2.213c3.713-3.153 5.876-7.676 5.876-12.537 0-4.86-2.163-9.384-5.876-12.537zm-2.163 22.622a1.452 1.452 0 0 1-1.926-2.172 10.54 10.54 0 0 0 0-15.826 1.451 1.451 0 1 1 1.926-2.172c2.877 2.55 4.554 6.185 4.554 10.085 0 3.9-1.677 7.535-4.554 10.085z"
          />
        </g>
      </svg>
      <circle
        className={cx('circle-background')}
        cx={width / 2}
        cy={width / 2}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
        stroke={color || undefined}
      />
      <circle
        className={cx('circle-progress')}
        cx={width / 2}
        cy={width / 2}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
        stroke={progressColor}
        // Start progress marker at 12 O'Clock
        transform={`rotate(-90 ${width / 2} ${width / 2})`}
        style={{
          strokeDasharray: dashArray,
          strokeDashoffset: dashOffset,
        }}
      />
    </svg>
  )
}

export default CircularProgressIcon
